import { ACCESS_KEY, Data } from 'core/utilities';
import { HTTPMethod, mutate, query, Result } from '.';
import { axios, isSuccessCode } from 'core/api';

export const addInvestment = async (token : string, applicationId : string, payload : Data) : Promise<Result> => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/applicant/investment/`, 
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.POST
    );

    return reply;
};

export const updateInvestment = async (token : string, applicationId : string, investmentId : string, payload : Data) : Promise<Result> => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/applicant/investment/${investmentId}/`, 
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.PATCH
    );

    return reply;
};

export const addMultipleInvestments = async (applicationId : string, payload : Data) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        let hasSucceeded = true;

        for (const investmentAccount of Object.values(payload)) {
            const { id, account } = investmentAccount;

            if (id === null) {
                const { success } = await addInvestment(token, applicationId, account);
                if (!success) {
                    hasSucceeded = false;
                    break;
                }
            } else {
                const { success } = await updateInvestment(token, applicationId, id, account);
                if (!success) {
                    hasSucceeded = false;
                    break;
                }
            }
        }

        return {
            success : hasSucceeded
        };
    }

    return {
        success : false
    };
};

export const updateDebt = async (token : string, applicationId: string, debtId : string, payload : Data) : Promise<Result> => {
    const reply = mutate(
        `api/v1/mortgage/${applicationId}/applicant/debt/${debtId}/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.PATCH
    );

    return reply;
};

export const createDebt = async (token : string, applicationId: string, payload : Data) : Promise<Result> => {
    const reply = mutate(
        `api/v1/mortgage/${applicationId}/applicant/debt/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.POST
    );

    return reply;
};

export const upsertDebt = async (debt : Data, applicationId : string, debtId? : string) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        let success = false;

        if (debtId) {
            const reply = await updateDebt(token, applicationId, debtId, debt);
            success = reply.success;
        } else {
            const reply = await createDebt(token, applicationId, debt);
            success = reply.success;
        }

        return {
            success
        };
    }

    return {
        success : false
    };
};

export const updateApplication = async (applicationId: string, payload : Data) => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        const { success } = await mutate(
            `api/v1/mortgage/${applicationId}/`,
            payload,
            { 'Authorization' : `Bearer ${token}` },
            HTTPMethod.PATCH
        );

        return {
            success
        };
    }

    return {
        success : false
    };
};

export const createTargetMortgageProperty = async (token : string, applicationId : string, payload : Data) => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/property/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.POST
    );

    return reply;
};

export const updateTargetMortgageProperty = async (token : string, applicationId : string, propertyId : string, payload : Data) => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/property/${propertyId}/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.PATCH
    );

    return reply;
};

export const upsertTargetMortgageProperty = async (applicationId : string, payload : Data) => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        const { propertyId, ...data } = payload;

        if (propertyId) {
            return await updateTargetMortgageProperty(token, applicationId, propertyId, data);
        } else {
            return await createTargetMortgageProperty(token, applicationId, data);
        }
    }

    return {
        success : false
    };
};

export const createMortgageDownpayment = async (token : string, applicationId : string, payload : Data) => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/downpayment/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.POST
    );

    return reply;
};

export const updateMortgageDownpayment = async (token : string, applicationId : string, downpaymentId : string, payload : Data) => {
    const reply = await mutate(
        `api/v1/mortgage/${applicationId}/downpayment/${downpaymentId}/`,
        payload,
        { 'Authorization' : `Bearer ${token}` },
        HTTPMethod.PATCH
    );

    return reply;
};

export const upsertTargetMortgagePropertyDetails = async (applicationId : string, payload : Data) : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        const { application, property, downpayment } = payload;
        let hasSucceeded = true;

        if (application) {
            const { success } = await updateApplication(applicationId, application);
            hasSucceeded = hasSucceeded && success;
        }

        if (hasSucceeded && property) {
            const { id, data } = property;

            if (id) {
                const { success } = await updateTargetMortgageProperty(token, applicationId, id, data);
                hasSucceeded = hasSucceeded && success;
            } else {
                const { success } = await createTargetMortgageProperty(token, applicationId, data);
                hasSucceeded = hasSucceeded && success;
            }
        }

        if (hasSucceeded && downpayment) {
            const { id, data } = downpayment;

            if (id) {
                const { success } = await updateMortgageDownpayment(token, applicationId, id, data);
                hasSucceeded = hasSucceeded && success;
            } else {
                const { success } = await createMortgageDownpayment(token, applicationId, data);
                hasSucceeded = hasSucceeded && success;
            }
        }

        return {
            success : hasSucceeded
        };
    }

    return {
        success : false
    };
};

export const getWaitlistPosition = async () : Promise<Result> => {
    const token = localStorage.getItem(ACCESS_KEY);

    if (token) {
        return await query(
            'api/v1/waiting-list',
            { 'Authorization' : `Bearer ${token}` }
        );
    }

    return {
        success : false
    };
};

export const completeApplication = async (applicationId: string) => {
    const response = await axios.get(`/api/v1/mortgage/${applicationId}/apply/`);

    const { status } = response;

    if (status === 200) {
        return isSuccessCode(status);
    } else {
        return false;
    }
};
